// Companies (B2B accounts). List + create + edit in a modal. Contacts list
// shows users.company_id linkage but assignment lives in the Users editor.
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useToast, ToastBanner } from '../../components/Toast'
import { apiFetch } from '../../lib/api'

interface Company {
  id: string; name: string; abn: string | null; billingEmail: string | null
  paymentTerms: string | null; notes: string | null; isActive: boolean; createdAt: string
}

const PAYMENT_TERMS = ['cod','net7','net14','net30']

export default function Companies() {
  const { getAccessToken } = useAuth()
  const [items, setItems] = useState<Company[]>([])
  const [search, setSearch] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [editing, setEditing] = useState<Company | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Partial<Company>>({})
  const { toast, show } = useToast()

  async function load() {
    try {
      const d = await apiFetch<{ items: Company[] }>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/companies${search ? `?search=${encodeURIComponent(search)}` : ''}`)
      setItems(d.items); setErr(null)
    } catch (e) { setErr((e as Error).message) }
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  function openCreate() { setDraft({ name: '', abn: '', billingEmail: '', paymentTerms: 'net14', notes: '', isActive: true }); setCreating(true) }
  function openEdit(c: Company) { setDraft(c); setEditing(c) }

  async function save() {
    if (!draft.name) { alert('Name required'); return }
    try {
      if (editing) {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/companies/${editing.id}`, { method: 'PATCH', body: JSON.stringify(draft) })
      } else {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/companies', { method: 'POST', body: JSON.stringify(draft) })
      }
      setCreating(false); setEditing(null); await load()
      show(editing ? 'Company updated.' : 'Company created.')
    } catch (e) { show((e as Error).message, 'error', 5000) }
  }

  const isModalOpen = creating || !!editing

  return (
    <div className="p-8">
      <ToastBanner toast={toast} />
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Customers</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Companies</h1>
        </div>
        <div className="flex gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="input max-w-xs" />
          <button onClick={openCreate} className="btn-gold">+ New company</button>
        </div>
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Name</th>
              <th className="text-left px-4 py-2 font-bold">ABN</th>
              <th className="text-left px-4 py-2 font-bold">Billing</th>
              <th className="text-left px-4 py-2 font-bold">Terms</th>
              <th className="text-left px-4 py-2 font-bold">Status</th>
              <th className="text-right px-4 py-2 font-bold">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No companies yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-surface transition-colors cursor-pointer" onClick={() => openEdit(c)}>
                <td className="px-4 py-3 text-ink font-semibold">{c.name}</td>
                <td className="px-4 py-3 text-ink-2 font-mono text-xs">{c.abn ?? '—'}</td>
                <td className="px-4 py-3 text-ink-2">{c.billingEmail ?? '—'}</td>
                <td className="px-4 py-3 text-ink-2">{c.paymentTerms ?? '—'}</td>
                <td className="px-4 py-3"><span className={'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' + (c.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600')}>{c.isActive ? 'active' : 'archived'}</span></td>
                <td className="px-4 py-3 text-right text-xs text-ink-3">{new Date(c.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading uppercase font-bold text-lg text-ink mb-4">{editing ? 'Edit company' : 'New company'}</h3>
            <div className="space-y-3">
              <div><div className="eyebrow">Name</div><input value={draft.name ?? ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">ABN</div><input value={draft.abn ?? ''} onChange={(e) => setDraft({ ...draft, abn: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">Billing email</div><input type="email" value={draft.billingEmail ?? ''} onChange={(e) => setDraft({ ...draft, billingEmail: e.target.value })} className="input" /></div>
              <div><div className="eyebrow">Payment terms</div>
                <select value={draft.paymentTerms ?? ''} onChange={(e) => setDraft({ ...draft, paymentTerms: e.target.value })} className="input">
                  <option value="">—</option>
                  {PAYMENT_TERMS.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              <div><div className="eyebrow">Notes</div><textarea rows={3} value={draft.notes ?? ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="input" /></div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-ink-2">
                  <input type="checkbox" checked={draft.isActive ?? true} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
                  Active
                </label>
              )}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setCreating(false); setEditing(null) }} className="btn-outline flex-1 justify-center">Cancel</button>
              <button onClick={save} className="btn-gold flex-1 justify-center">{editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
